import React, { useEffect } from 'react';
import clsx from 'clsx';
import _ from 'underscore';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { Button, Card, CardHeader, CardContent, CardActions, Grid, Divider, TextField, colors } from '@mui/material';
import DatePicker from 'components/DatePicker/DatePicker';
import { SelectField } from 'components';
import validate from 'validate.js';

import { enqueueSnackbar } from 'actions';
import { ReportRFCounters, SetFormState } from '../../actions/ReportRFCountersActions';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	container: {
		marginTop: theme.spacing(3),
	},
	tabs: {
		marginTop: theme.spacing(3),
	},
	content: {
		marginTop: theme.spacing(3),
	},
	generate: {
		marginLeft: 'auto',
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const schema = {
	site: {
		presence: { allowEmpty: false, message: 'is required' }, length: { maximum: 360 }
	},
	startDate: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	pre: {
		presence: { allowEmpty: false, message: 'is required' }, numericality: { greaterThanOrEqualTo: 1, lessThanOrEqualTo: 15 }
	},
	post: {
		presence: { allowEmpty: false, message: 'is required' }, numericality: { greaterThanOrEqualTo: 1, lessThanOrEqualTo: 15 }
	},
	reportType: {
		presence: { allowEmpty: false, message: 'is required' },
	}
};

const reportType = [
	{ value: 'Daily', text: 'Daily' },
	{ value: 'Hourly', text: 'Hourly' }
];

const ReportRFCountersForm = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.ranReportRFReducer.formState);

	useEffect(() => {
		const errors = validate(formState.values, schema);

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
		});
	}, [formState.values]);

	const _handleChange = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleDateChange = (value, name) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[name]: value,
			},
			touched: {
				...formState.touched,
				[name]: true,
			},
		});
	};

	const _hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));
	const user = useSelector(state => state.session.user);
	const _generate = () => {
		dispatch(ReportRFCounters(formState.values, user.id));
	};

	return (
		<Card className={classes.content}>
			<CardHeader title="Counters Report" />
			<Divider />
			<CardContent>
				<Grid container spacing={1}>
					<Grid item md={7} xs={12}>
						<TextField
							fullWidth
							label="Site(s) List"
							name="site"
							onChange={_handleChange}
							value={formState.values.site}
							variant="outlined"
							error={_hasError('site')}
							helperText={
								_hasError('site') ? formState.errors.site[0] : 'Example: 10500,10501,10502'
							}
						/>
					</Grid>
					<Grid item md={1} xs={12}>
						<TextField
							fullWidth
							label="PRE"
							name="pre"
							onChange={_handleChange}
							value={formState.values.pre}
							variant="outlined"
							error={_hasError('pre')}
							helperText={
								_hasError('pre') ? formState.errors.pre[0] : 'Example: 7'
							}
						/>
					</Grid>
					<Grid item md={1} xs={12}>
						<TextField
							fullWidth
							label="POST"
							name="post"
							onChange={_handleChange}
							value={formState.values.post}
							variant="outlined"
							error={_hasError('post')}
							helperText={
								_hasError('post') ? formState.errors.post[0] : 'Example: 7'
							}
						/>
					</Grid>
					<Grid item md={2} xs={12}>
						<DatePicker
							label="Implementation Date"
							name="startDate"
							value={formState.values.startDate || null}
							onChange={(value) => _handleDateChange(value, 'startDate')}
							error={_hasError('startDate')}
							helperText={_hasError('startDate') ? formState.errors.startDate[0] : null}
						/>
					</Grid>
					<Grid item md={1} xs={12}>
						<SelectField
							id="reportType"
							name="reportType"
							value={formState.values.reportType || ''}
							onChange={_handleChange}
							hintText="Report Type"
							menuItems={reportType}
							error={_hasError('reportType')}
							helperText={_hasError('reportType') ? formState.errors.reportType[0] : null}
						/>
					</Grid>
				</Grid>
			</CardContent>
			<Divider />
			<CardActions>
				<Button className={classes.generate} onClick={_generate} variant="contained" disabled={!formState.isValid}>Export</Button>
			</CardActions>
		</Card>
	);
};

export default ReportRFCountersForm;
